import { Soundfont2 } from "smplr";
import type { PlaybackNote } from "@midi-fantom-utils/midi-util";
import type { SoundfontSource } from "./soundfontSource.js";
import { loadSoundfontBytes } from "./soundfontSource.js";
import type { ProgramMap } from "./programMap.js";
import { parseSf2 } from "./sf2.js";
import { buildProgramMap } from "./programMap.js";

export type Channel = { program: number; isDrum: boolean };

export type EngineState = {
  readonly isLoading: boolean;
  readonly isReady: boolean;
  readonly error: string | null;
  readonly progress: { readonly loaded: number; readonly total: number };
  /**
   * Каналы, для которых `loadInstrument` бросил ошибку (preset не найден
   * в SF2). Эти каналы не будут воспроизводить ноты, но движок всё равно
   * переходит в `isReady: true`. UI может показать warning.
   */
  readonly failedChannels: ReadonlyArray<Channel>;
  /**
   * Имя пресета, который движок подобрал для каждого уникального
   * `(program, isDrum)` канала. Ключ — `${isDrum?"d":"m"}:${program}`,
   * значение — имя пресета из SF2 или `null` для failed-каналов
   * (drum без drum-кита, melodic fallback тоже даёт null).
   *
   * Используется диагностическим UI (`MidiPlayer` → `PlaybackEventTable`)
   * для отображения реального имени инструмента, в который уходит нота —
   * чтобы пользователь мог сравнить с исходным `program` и понять, не
   * фоллбэчится ли канал на «piano» и не молчит ли drum-канал.
   *
   * Заполняется там же, где `loadInstrument` (т.е. после успешного
   * парсинга SF2). Инвалидируется при `setInput`.
   */
  readonly resolvedPresets: ReadonlyMap<string, string | null>;
};

export type SoundfontEngine = {
  getState(): EngineState;
  subscribe(listener: () => void): () => void;
  playNote(note: PlaybackNote, whenSec: number, durationSec: number): void;
  stopAll(): void;
  /** Подменить источник SF2, ноты или выход synth'ов (для piano-roll / gain). */
  setInput(input: {
    source: SoundfontSource;
    notes: ReadonlyArray<PlaybackNote>;
    destination?: AudioNode | null;
  }): void;
  dispose(): void;
};

export type SoundfontEngineOptions = {
  audioCtx: AudioContext;
  source: SoundfontSource;
  notes: ReadonlyArray<PlaybackNote>;
  /**
   * Опциональный `AudioNode` для подключения выхода каждого `Soundfont2`.
   * Если не задан — smplr подключает synth'и к `audioCtx.destination`
   * напрямую. Используется UI-плеером, чтобы пропустить звук через свой
   * master `GainNode` (регулятор громкости). Смена значения на лету
   * вызывает перезагрузку движка (через `setInput`).
   */
  destination?: AudioNode;
  /**
   * Если true — конструктор НЕ запускает начальную загрузку. Консьюмер
   * обязан вызвать `setInput(...)` сразу после создания, чтобы запустить
   * загрузку. Полезно когда React-эффект всё равно вызывает `setInput`
   * после mount — иначе первый fetch тратится впустую, потому что
   * `setInput` всё равно делает epoch-cancel первой попытки.
   */
  defer?: boolean;
};

const FALLBACK_PROGRAM = 0;

const uniqueChannels = (notes: ReadonlyArray<PlaybackNote>): Channel[] => {
  const map = new Map<string, Channel>();
  for (const n of notes) {
    const isDrum = n.isDrum;
    const program = isDrum ? 0 : n.program;
    const key = `${isDrum ? "d" : "m"}:${program}`;
    if (!map.has(key)) map.set(key, { program, isDrum });
  }
  return Array.from(map.values());
};

const resolveInstrumentName = (
  map: ProgramMap,
  channel: Channel
): { name: string | null; isFallback: boolean } => {
  if (channel.isDrum) {
    const drumKit = map.getDrumKit();
    if (drumKit) return { name: drumKit, isFallback: false };
    // Drum-канал в мелодическом SF2 — НЕ пытаемся melodic fallback
    // (фортепиано на ударной дорожке — это хуже тишины; помечаем канал
    // как failed, UI покажет warning и канал промолчит).
    return { name: null, isFallback: true };
  }
  const melodic = map.getMelodic(channel.program);
  if (melodic) return { name: melodic, isFallback: false };
  return { name: map.getMelodic(FALLBACK_PROGRAM), isFallback: true };
};

const channelKey = (c: Channel): string =>
  `${c.isDrum ? "d" : "m"}:${c.program}`;

const shallowEqualState = (a: EngineState, b: EngineState): boolean => {
  if (a.isLoading !== b.isLoading) return false;
  if (a.isReady !== b.isReady) return false;
  if (a.error !== b.error) return false;
  if (a.progress.loaded !== b.progress.loaded) return false;
  if (a.progress.total !== b.progress.total) return false;
  if (a.failedChannels.length !== b.failedChannels.length) return false;
  for (let i = 0; i < a.failedChannels.length; i++) {
    if (a.failedChannels[i]!.program !== b.failedChannels[i]!.program) return false;
    if (a.failedChannels[i]!.isDrum !== b.failedChannels[i]!.isDrum) return false;
  }
  if (a.resolvedPresets.size !== b.resolvedPresets.size) return false;
  for (const [k, v] of a.resolvedPresets) {
    if (b.resolvedPresets.get(k) !== v) return false;
  }
  return true;
};

const EMPTY_STATE: EngineState = Object.freeze({
  isLoading: false,
  isReady: false,
  error: null,
  progress: Object.freeze({ loaded: 0, total: 0 }),
  failedChannels: Object.freeze([]),
  resolvedPresets: Object.freeze(new Map<string, string | null>()),
});

// Минимальный тип, которым мы пользуемся из smplr.Soundfont2. Реальный
// `ReturnType<typeof Soundfont2>` тоже подойдёт, но в jsdom-тестах мы
// мокаем модуль `smplr` целиком — этот интерфейс достаточно узкий, чтобы
// тестовый stub удовлетворял ему без проблем.
type SoundfontSynth = {
  start(opts: {
    note: number;
    time: number;
    duration: number;
    velocity: number;
  }): void;
  stop(): void;
  dispose(): void;
  loadInstrument(name: string): Promise<void>;
  ready: Promise<unknown>;
};

export const createSoundfontEngine = (
  initial: SoundfontEngineOptions
): SoundfontEngine => {
  let audioCtx: AudioContext = initial.audioCtx;
  let source: SoundfontSource = initial.source;
  let notes: ReadonlyArray<PlaybackNote> = initial.notes;
  let destination: AudioNode | null = initial.destination ?? null;

  const instances = new Map<string, SoundfontSynth>();
  let blobUrl: string | null = null;

  let generation = 0;
  let disposed = false;
  let cachedState: EngineState = EMPTY_STATE;

  const listeners = new Set<() => void>();

  const setState = (next: EngineState) => {
    if (shallowEqualState(cachedState, next)) return;
    cachedState = Object.freeze({
      isLoading: next.isLoading,
      isReady: next.isReady,
      error: next.error,
      progress: Object.freeze({
        loaded: next.progress.loaded,
        total: next.progress.total,
      }),
      failedChannels: Object.freeze([...next.failedChannels]),
      resolvedPresets: Object.freeze(new Map(next.resolvedPresets)),
    });
    for (const l of listeners) l();
  };

  const disposeInstances = (map: Map<string, SoundfontSynth>) => {
    for (const s of map.values()) {
      try {
        s.dispose();
      } catch {
        // ignore — synth may be in a torn-down state
      }
    }
    map.clear();
  };

  const releaseBlobUrl = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      blobUrl = null;
    }
  };

  const fullCleanup = () => {
    disposeInstances(instances);
    releaseBlobUrl();
    setState(EMPTY_STATE);
  };

  const startLoad = () => {
    if (disposed) return;
    const myGen = generation;

    const channels = uniqueChannels(notes);
    if (channels.length === 0) {
      // Никаких звуков не требуется — сразу ready.
      disposeInstances(instances);
      releaseBlobUrl();
      setState({
        isLoading: false,
        isReady: true,
        error: null,
        progress: { loaded: 0, total: 0 },
        failedChannels: [],
        resolvedPresets: new Map(),
      });
      return;
    }

    setState({
      isLoading: true,
      isReady: false,
      error: null,
      progress: { loaded: 0, total: channels.length },
      failedChannels: [],
      resolvedPresets: new Map(),
    });

    (async () => {
      const newInstances = new Map<string, SoundfontSynth>();
      try {
        const bytes = await loadSoundfontBytes(source);
        if (myGen !== generation || disposed) return;

        const programMap = buildProgramMap(bytes);

        // `bytes.slice()` возвращает `Uint8Array<ArrayBuffer>` (свежий
        // ArrayBuffer, а не `ArrayBufferLike`), что assignable к `BlobPart`
        // в TS 5.7+ / strict-lib. Семантически эквивалентно: данные те же.
        const blob = new Blob([bytes.slice()], {
          type: "application/octet-stream",
        });
        const newBlobUrl = URL.createObjectURL(blob);
        if (myGen !== generation || disposed) {
          URL.revokeObjectURL(newBlobUrl);
          return;
        }

        const failed: Channel[] = [];
        const resolvedMap = new Map<string, string | null>();
        let loaded = 0;
        let cancelled = false;
        const totalChannels = channels.length;

        // Все `Soundfont2`-инстансы парсятся и загружаются параллельно.
        // Последовательная загрузка делала `isReady` недостижимым для
        // файлов с >5 каналов (например, `159527.mid` с 16 каналами
        // ждёт ≥16×SF2 parse → 8–30 с тишины → пользователь слышит
        // фрагменты, т.к. ноты идут в синты по мере готовности — это
        // воспринимается как «мешанина»). Каждый инстанс независим
        // (парсит SF2 из общего blob URL), Promise.all безопасен.
        await Promise.allSettled(
          channels.map(async (ch) => {
            if (myGen !== generation || disposed) {
              cancelled = true;
              return;
            }
            const synth = Soundfont2(audioCtx, {
              url: newBlobUrl,
              createSoundfont: parseSf2 as (data: Uint8Array) => never,
              destination: destination ?? undefined,
            }) as SoundfontSynth;
            newInstances.set(channelKey(ch), synth);
            // smplr'овский `Soundfont2` лениво парсит SF2 при первом обращении:
            // `loadInstrument()` находит пресет по `instrumentNames`, который
            // заполняется ТОЛЬКО после `await synth.ready`. Без этого
            // `loadInstrument` бросает `instrument "..." not found`.
            await synth.ready;
            if (myGen !== generation || disposed) {
              cancelled = true;
              return;
            }
            const resolved = resolveInstrumentName(programMap, ch);
            const key = channelKey(ch);
            if (resolved.name) {
              try {
                await synth.loadInstrument(resolved.name);
                resolvedMap.set(key, resolved.name);
              } catch (loadErr) {
                failed.push(ch);
                resolvedMap.set(key, null);
                if (myGen === generation && !disposed) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    `createSoundfontEngine: не удалось загрузить пресет "${resolved.name}" для program=${ch.program} (isDrum=${ch.isDrum})`,
                    loadErr
                  );
                }
              }
            } else {
              // `resolveInstrumentName` вернул null только если это
              // drum-канал в мелодическом SF2 (см. `resolveInstrumentName`).
              // Для melodic fallback (FALLBACK_PROGRAM=0) есть piano по
              // умолчанию в любом осмысленном SF2, так что resolved.name
              // для melodic всегда не null.
              failed.push(ch);
              resolvedMap.set(key, null);
            }
            loaded += 1;
            if (myGen === generation && !disposed) {
              setState({
                isLoading: true,
                isReady: false,
                error: null,
                progress: { loaded, total: totalChannels },
                failedChannels: failed,
                resolvedPresets: resolvedMap,
              });
            }
          })
        );

        if (cancelled || myGen !== generation || disposed) {
          disposeInstances(newInstances);
          URL.revokeObjectURL(newBlobUrl);
          return;
        }

        // Коммитим: освобождаем старые, ставим новые.
        disposeInstances(instances);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        instances.clear();
        for (const [k, v] of newInstances) instances.set(k, v);
        blobUrl = newBlobUrl;

        setState({
          isLoading: false,
          isReady: true,
          error: null,
          progress: { loaded: totalChannels, total: totalChannels },
          failedChannels: failed,
          resolvedPresets: resolvedMap,
        });
      } catch (err) {
        if (myGen !== generation || disposed) return;
        const msg = err instanceof Error ? err.message : String(err);
        disposeInstances(newInstances);
        setState({
          isLoading: false,
          isReady: false,
          error: `Не удалось загрузить soundfont: ${msg}`,
          progress: { loaded: 0, total: channels.length },
          failedChannels: [],
          resolvedPresets: new Map(),
        });
      }
    })();
  };

  // Decide initial action.
  if (!initial.defer && audioCtx && source) {
    startLoad();
  } else {
    setState(EMPTY_STATE);
  }

  return {
    getState: () => cachedState,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    playNote: (note, when, durationSec) => {
      const isDrum = note.isDrum;
      const program = isDrum ? 0 : note.program;
      const key = `${isDrum ? "d" : "m"}:${program}`;
      const synth = instances.get(key);
      if (!synth) return;
      try {
        synth.start({
          note: note.midi,
          time: when,
          duration: Math.max(0.01, durationSec),
          // smplr ожидает MIDI velocity 0..127 (НЕ нормализованную 0..1).
          velocity: note.velocity,
        });
      } catch (err) {
        // Audio hot path — не трогаем глобальный EngineState (там может
        // идти прогресс загрузки или активный error), просто предупреждаем.
        const msg = err instanceof Error ? err.message : String(err);
        // eslint-disable-next-line no-console
        console.warn(`createSoundfontEngine: ошибка воспроизведения: ${msg}`);
      }
    },
    stopAll: () => {
      for (const synth of instances.values()) {
        try {
          synth.stop();
        } catch {
          // ignore — may already be stopped
        }
      }
    },
    setInput: (input) => {
      generation++;
      source = input.source;
      notes = input.notes;
      const newDestination = input.destination ?? null;
      const destinationChanged = newDestination !== destination;
      destination = newDestination;
      if (destinationChanged) {
        // Смена выхода synth'ов требует пересоздания инстансов — старые
        // Soundfont2 уже подключены к старому destination.
        fullCleanup();
      }
      if (input.source) {
        startLoad();
      }
    },
    dispose: () => {
      disposed = true;
      generation++;
      fullCleanup();
      listeners.clear();
    },
  };
};